import { useEffect, useState } from "react";
import { api, fmtDistance, googleMapsUrl, osaLabel } from "../api";
import type { Coords, Tag, VenueDetail as V } from "../types";

const LIQUOR: Record<string, string> = {
  yes: "有",
  no: "冇",
  byo: "可以自己帶酒",
  unknown: "未知",
};

const AREA: Record<string, string> = {
  open_air: "無蓋露天",
  covered_terrace: "有蓋露台",
  rooftop: "天臺",
  indoor: "室內",
};

/**
 * 詳情頁。次序係確認過嘅：店家資料行先，然後先係露天座位（只有許可時段
 * 同來源，唔顯示牌照號碼），再係你嘅紀錄，最後到訪歷史。
 * 官方資料同你嘅紀錄一定要分開 —— 兩者可信度唔同。
 */
export default function Detail({
  id,
  coords,
  onBack,
  onLog,
}: {
  id: number;
  coords: Coords;
  onBack: () => void;
  onLog: (name: string) => void;
}) {
  const [v, setV] = useState<V | null>(null);
  const [err, setErr] = useState("");
  const [tags, setTags] = useState<Tag[]>([]);

  // 標籤 key → 中文 label。由後端封閉清單嚟（session 內只攞一次），
  // 前端唔可以自己寫一份，否則兩邊會漂移。
  useEffect(() => {
    let live = true;
    api.tags().then((t) => live && setTags(t)).catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const tagLabel = (key: string) =>
    tags.find((t) => t.key === key)?.label_zh ?? key;

  useEffect(() => {
    let live = true;
    api
      .venue(id, coords)
      .then((d) => live && setV(d))
      .catch((e) => live && setErr(e instanceof Error ? e.message : "載入失敗"));
    return () => {
      live = false;
    };
  }, [id, coords?.lat, coords?.lng]);

  if (err) return <div className="app__body"><div className="err">{err}</div></div>;
  if (!v) return <div className="loading">載入緊…</div>;

  const verdictLabel =
    v.cigar_status === "yes" ? "食得" : v.cigar_status === "no" ? "唔得" : "未確定";
  const verdictClass =
    v.cigar_status === "yes" ? "mine__verdict--yes"
    : v.cigar_status === "no" ? "mine__verdict--no" : "";

  return (
    <>
      <div className="sign sign--compact">
        <button className="sign__where" onClick={onBack} style={{ opacity: 0.9 }}>
          ← 返回
        </button>
        <div className="sign__title">{v.name}</div>
        <div className="sign__sub" style={{ marginTop: 8 }}>
          <span style={{ display: "block", overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {[v.district, v.premises_type, fmtDistance(v.distance_m)].filter(Boolean).join("　·　")}
          </span>
        </div>
      </div>

      <div className="app__body">
        {/* 店家資料 —— 最頂 */}
        <div className="sec">店家資料</div>
        <table className="table">
          <tbody>
            <tr><td>菜式</td><td>{v.cuisines.join("　") || "—"}</td></tr>
            <tr><td>店舖種類</td><td>{v.premises_type ?? "—"}{v.shop ? `　·　${v.shop}` : ""}</td></tr>
            <tr><td>營業時間</td><td>{v.opening_hours_zh ?? v.opening_hours_text ?? "—"}</td></tr>
            <tr><td>電話</td><td className="tel">
              {v.phone
                ? <a href={`tel:${v.phone}`} style={{ color: "inherit" }}>{v.phone}　按一下撥號</a>
                : "—"}
            </td></tr>
            <tr><td>酒牌</td><td>{LIQUOR[v.liquor] ?? "未知"}</td></tr>
            {/* 呢個係 OpenRice 收錄、餐廳自己申報嘅標籤 —— 唔係官方認證 */}
            {v.pet_friendly && (
              <tr><td>寵物</td><td>寵物友善（餐廳申報）</td></tr>
            )}
            {v.address && (
              <tr>
                <td>地址</td>
                <td className="addr">
                  {/* 個掣要真係喺地址右手邊。用 inline 排列嘅話，地址一換行個掣
                      就會跌落第三行，所以要用 flex 令佢靠右並對齊第一行。 */}
                  <div className="addr__row">
                    <span className="addr__text">{v.address}</span>
                    <a className="mapbtn mapbtn--inline"
                       href={googleMapsUrl(v.name, v.address)}
                       target="_blank" rel="noopener noreferrer">
                      <svg width="9" height="11" viewBox="0 0 9 11" fill="none" aria-hidden="true">
                        <path d="M4.5 0C2 0 0 2 0 4.5 0 7.9 4.5 11 4.5 11S9 7.9 9 4.5C9 2 7 0 4.5 0z"
                          stroke="currentColor" strokeWidth="1.1" />
                        <circle cx="4.5" cy="4.4" r="1.5" stroke="currentColor" strokeWidth="1.1" />
                      </svg>
                      Google Maps
                    </a>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* 露天座位 —— 只講許可時段同來源 */}
        <div className="outseat">
          <div className="outseat__head">
            <span className="outseat__title">露天座位</span>
            <span className="outseat__badge">食環署</span>
          </div>
          <div className="outseat__row">
            <span className="outseat__key">許可時段</span>
            <span className="outseat__val" style={v.osa_multi
              ? { fontFamily: "var(--hei)", fontSize: 12.5, fontWeight: 400 } : undefined}>
              {osaLabel(v)}
            </span>
          </div>
          {v.osa_multi && (
            <div style={{ fontSize: 10, color: "var(--ink2)", marginTop: 4 }}>
              牌照原文分幾種時段，以上照錄。
            </div>
          )}
          {v.shop_closes && (
            <div className="outseat__row" style={{ marginTop: 6 }}>
              <span className="outseat__key">店舖</span>
              <span style={{ fontFamily: "var(--ming)", fontSize: 12 }}>
                {v.shop_closes} 收
              </span>
            </div>
          )}
          <div className="outseat__src">摘錄自食環署</div>
        </div>

        {/* 你嘅紀錄 —— 同官方分開 */}
        <div className="mine">
          <div className="mine__title">你嘅紀錄　（非官方）</div>
          <div className={`mine__verdict ${verdictClass}`}>
            {verdictLabel}
            {v.cigar_area_type && `　·　${AREA[v.cigar_area_type] ?? v.cigar_area_type}`}
          </div>
          <div className="mine__meta">
            {v.last_visited
              ? `最後確認：${v.last_visited}　·　去過 ${v.visit_count} 次`
              : "未去過　·　未有紀錄"}
          </div>
        </div>

        {/* 到訪歷史 */}
        {v.visits.length > 0 && (
          <>
            <div className="sec">去過幾次</div>
            {v.visits.map((vi) => (
              <div key={vi.id} style={{ borderLeft: "1px solid var(--line)",
                padding: "7px 0 7px 11px", fontSize: 11.5, lineHeight: 1.6 }}>
                <div style={{ fontFamily: "var(--ming)", color: "var(--brass)", fontSize: 11.5 }}>
                  {vi.visited_on}
                </div>
                {vi.remark}
                {vi.tags.length > 0 && (
                  <div className="chips" style={{ marginTop: 5 }}>
                    {vi.tags.map((k) => (
                      <span className="chip chip--static" key={k}>{tagLabel(k)}</span>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 9.5, color: "var(--ink2)", marginTop: 3 }}>
                  {vi.who === "me" ? "你" : "朋友"}
                  {vi.photos.length > 0 && `　·　${vi.photos.length} 張相`}
                </div>
              </div>
            ))}
          </>
        )}

        <div className="sec">資料來源</div>
        <div style={{ fontSize: 10.5, color: "var(--ink2)", lineHeight: 1.7 }}>
          {v.confidence ?? "—"}
          {v.licence_count > 1 && `　·　同一地址有 ${v.licence_count} 個牌照`}
        </div>
      </div>

      <div className="footerbar">
        <button className="btn" onClick={() => onLog(v.name)}>記錄今日到訪</button>
      </div>
    </>
  );
}
